import type { McqQuestion } from '../../fetchSurvey/types/mcq';
import type { ScaleColumn } from '../../fetchSurvey/types/shared';
import type { AnswerValue, MatrixRowAnswers, SurveyAnswers } from '../types';
import {
  buildMcqOptionLevelKey,
  formatMcqOtherSubmitText,
  isMcqOtherOptionSelected,
  resolveMcqSelectedOptionIds,
} from '../../surveyLogic/fields/mcqOtherUtils';

type ScalePoint = { id: string; optionLabel: string };

export function matrixColumnStoredValue(column: ScaleColumn): string | null {
  return column.id === 'na' ? null : column.id;
}

function deriveScalePointValue(option: ScalePoint, index: number): number {
  const numeric = Number(option.optionLabel);
  return Number.isFinite(numeric) && option.optionLabel.trim() !== '' ? numeric : index;
}

function resolveColumnSubmitText(columns: ScaleColumn[], storedValue: string | number): string {
  const storedStr = String(storedValue);
  const col = columns.find(c => c.id === storedValue || c.id === storedStr);

  if (!col) {
    return storedStr;
  }

  const numeric = Number(col.label);
  if (Number.isFinite(numeric) && col.label.trim() !== '') {
    return String(numeric);
  }

  return col.label;
}

export type McqSubmitResult = {
  questionLabels: string[];
  optionLevelAnswers: Record<string, string[]>;
};

export function resolveMcqSubmitValues(
  question: McqQuestion,
  value: AnswerValue,
  answers: SurveyAnswers = {}
): McqSubmitResult | null {
  const ids = resolveMcqSelectedOptionIds(question, value);

  if (ids.length === 0) {
    return null;
  }

  const questionLabels = ids.map(id => {
    const option = question.options.find(o => o.id === id);
    return option?.optionLabel ?? String(id);
  });

  const optionLevelAnswers: Record<string, string[]> = {};

  for (const option of question.options) {
    if (!option.otherOption) {
      continue;
    }

    if (!isMcqOtherOptionSelected(question, value, option.id)) {
      continue;
    }

    const key = buildMcqOptionLevelKey(question.id, option.id);
    const otherText = answers[key];
    if (typeof otherText === 'string' && otherText.trim() !== '') {
      optionLevelAnswers[key] = [formatMcqOtherSubmitText(option.otherOption, otherText.trim())];
    }
  }

  return {
    questionLabels,
    optionLevelAnswers,
  };
}

export function resolveMatrixCellSubmitValues(
  scaleColumns: ScaleColumn[],
  cellValue: MatrixRowAnswers[string]
): string[] | null {
  if (cellValue === undefined || cellValue === null) {
    return null;
  }

  const values = Array.isArray(cellValue) ? cellValue : [cellValue];
  const resolved = values
    .filter((v): v is string | number => v !== undefined && v !== null)
    .map(v => resolveColumnSubmitText(scaleColumns, v));

  return resolved.length > 0 ? resolved : null;
}

export function resolveScalePointSubmitValue(
  options: ScalePoint[],
  storedNumber: number
): string {
  const match = options.find((opt, index) => deriveScalePointValue(opt, index) === storedNumber);

  if (match) {
    const numeric = Number(match.optionLabel);
    if (Number.isFinite(numeric) && match.optionLabel.trim() !== '') {
      return String(numeric);
    }
    return match.optionLabel;
  }

  return String(storedNumber);
}

export function resolveTextInputSubmitValue(value: AnswerValue): string[] | null {
  if (typeof value !== 'string' || value === '') {
    return null;
  }

  return [value];
}
